import React, { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Typography,
  Box,
  Alert,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";

import { Surface, SectionTitle } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

// How each recorded action should read to a person scanning the timeline.
const ACTION_META = {
  "profile.update": { label: "Profile edited", tone: T.info },
  "bank.update": { label: "Bank details changed", tone: T.warning },
  "bank.reveal": { label: "Bank details viewed", tone: T.muted },
  "verification.update": { label: "Verification changed", tone: T.primary },
  "takingOrders.update": { label: "Order-taking changed", tone: T.primary },
  "location.update": { label: "Location moved", tone: T.info },
  "document.add": { label: "Document added", tone: T.success },
  "document.remove": { label: "Document removed", tone: T.error },
  "pharmacy.softDelete": { label: "Pharmacy deleted", tone: T.error },
  "pharmacy.restore": { label: "Pharmacy restored", tone: T.success },
  "order.cancelRefund": { label: "Order cancelled / refunded", tone: T.error },
};

const meta = (action) =>
  ACTION_META[action] || { label: action, tone: T.muted };

// Values in `before`/`after` are whatever the endpoint recorded — a scalar
// for a field edit, an object for a bank or refund change.
function renderValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${v}`)
      .join(" · ");
  }
  return String(value);
}

function Entry({ row }) {
  const { label, tone } = meta(row.action);
  const who = row.actor?.email || row.actor?.name || row.actor?.id || "Unknown admin";
  const showsChange = row.before !== null || row.after !== null;

  return (
    <Box sx={{ display: "flex", gap: 1.5, py: 1.6 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: tone,
          mt: 0.8,
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: T.ink }}>
            {label}
          </Typography>
          {row.entity && (
            <Chip
              size="small"
              label={row.entity}
              sx={{
                fontSize: "0.68rem",
                height: 20,
                bgcolor: tint(tone, 0.1),
                color: tone,
              }}
            />
          )}
        </Stack>

        {showsChange && (
          <Typography sx={{ fontSize: "0.8rem", color: T.muted, mt: 0.4, wordBreak: "break-word" }}>
            <Box component="span" sx={{ color: T.subtle }}>
              {renderValue(row.before)}
            </Box>
            {" → "}
            <Box component="span" sx={{ color: T.ink, fontWeight: 600 }}>
              {renderValue(row.after)}
            </Box>
          </Typography>
        )}

        {row.reason && (
          <Typography sx={{ fontSize: "0.78rem", color: T.muted, mt: 0.4, fontStyle: "italic" }}>
            “{row.reason}”
          </Typography>
        )}

        <Typography sx={{ fontSize: "0.72rem", color: T.subtle, mt: 0.4 }}>
          {who} · {dayjs(row.at).format("DD MMM YYYY, hh:mm A")}
          {row.ip ? ` · ${row.ip}` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ActivityTab({ pharmacyId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (targetPage) => {
      setLoading(true);
      setError("");
      try {
        const res = await api.getActivity(pharmacyId, { page: targetPage, limit: 25 });
        // Page 1 replaces; later pages append, so "Load more" builds a list.
        setRows((prev) =>
          targetPage === 1 ? res.activity : [...prev, ...res.activity]
        );
        setTotal(res.pagination?.total || 0);
      } catch (err) {
        setError(err?.response?.data?.message || "Could not load activity.");
      } finally {
        setLoading(false);
      }
    },
    [pharmacyId]
  );

  useEffect(() => {
    setPage(1);
    load(1);
  }, [load]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  return (
    <Surface sx={{ p: { xs: 2, md: 3 } }}>
      <SectionTitle
        action={
          <Typography sx={{ fontSize: "0.75rem", color: T.subtle }}>
            {total} recorded
          </Typography>
        }
      >
        Activity
      </SectionTitle>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!rows.length && !loading ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ color: T.muted, fontSize: "0.88rem" }}>
            Nothing recorded yet. Changes made from this console appear here.
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {rows.map((row) => (
            <Entry key={row._id} row={row} />
          ))}
        </Stack>
      )}

      {rows.length < total && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            size="small"
            onClick={loadMore}
            disabled={loading}
            sx={{ textTransform: "none", color: T.secondary }}
          >
            {loading ? "Loading…" : `Load more (${total - rows.length} left)`}
          </Button>
        </Box>
      )}
    </Surface>
  );
}
